import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";
import { checkEmail } from "../lib/validation";
import GoogleLoginButton from "../components/GoogleLoginButton";
import PasswordInput from "../components/PasswordInput";
import api from "../lib/api";
import { KeyRound, X } from "lucide-react";

/**
 * Public sign-in page — for players & managers ONLY.
 * Admin/super-admin login is on a hidden /admin-login route (not linked anywhere).
 */
export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/";

    const [email, setEmail] = useState("");
    const [emailErr, setEmailErr] = useState("");
    const [emailSuggest, setEmailSuggest] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    // Forgot password modal
    const [showForgot, setShowForgot] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotBusy, setForgotBusy] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);

    const submitForgot = async (e) => {
        e.preventDefault();
        if (!forgotEmail) { toast.error("Enter your registered email"); return; }
        setForgotBusy(true);
        try {
            await api.post("/auth/forgot-password", { email: forgotEmail.trim().toLowerCase() });
            setForgotSent(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not send reset link");
        } finally {
            setForgotBusy(false);
        }
    };

    const closeForgot = () => { setShowForgot(false); setForgotEmail(""); setForgotSent(false); };

    const validateEmail = (v) => {
        const r = checkEmail(v);
        setEmailErr(r.ok ? "" : r.error);
        setEmailSuggest(r.ok && r.suggestion ? r.suggestion : "");
    };

    const submit = async (e) => {
        e.preventDefault();
        const r = checkEmail(email);
        if (!r.ok) { setEmailErr(r.error); return; }
        setLoading(true);
        try {
            const u = await login(email.trim().toLowerCase(), password);
            // Block admins on the public login page — they must use /admin-login
            if (["super_admin", "admin"].includes(u.role)) {
                toast.error("Admins must sign in via the admin portal.");
                navigate("/admin-login", { replace: true });
                return;
            }
            toast.success(`Welcome back, ${u.name}!`);
            // Iter 31 — Strict fork: managers ALWAYS land on /manager, never on
            // the player home, regardless of the `from` redirect path.
            const dest = u.role === "manager" ? "/manager" : (from?.startsWith("/manager") || from?.startsWith("/admin") ? "/" : from);
            navigate(dest, { replace: true });
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col md:flex-row bg-background">
            <Toaster theme="dark" richColors position="top-center" />
            <div className="hidden md:flex md:w-1/2 relative overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: "url(https://static.prod-images.emergentagent.com/jobs/9ec97b8b-c102-455e-b976-d7c2eab1132e/images/f2d82c4852b71397ad0f562b781e8bd61081b20444f9a408f388cfdb276d83c3.png)" }} />
                <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-950/70 to-slate-950/95" />
                <div className="relative z-10 p-12 flex flex-col justify-end">
                    <div className="text-[10px] font-bold tracking-[0.4em] text-[#00FF66] uppercase mb-3">Multi-sport turf booking</div>
                    <h1 className="font-display text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white leading-none">
                        Drive<br/>or<br/><span className="text-[#00FF66]">Kick.</span>
                    </h1>
                    <p className="mt-6 text-slate-300 max-w-md">
                        Book Football, Cricket, Padel, Badminton & more. Pay online, show up, play hard.
                    </p>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
                <div className="w-full max-w-md">
                    <div className="md:hidden mb-8 text-center">
                        <div className="font-display text-4xl font-bold uppercase text-white">Drive or <span className="text-[#00FF66]">Kick</span></div>
                    </div>
                    <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white">Sign in</h2>
                    <p className="text-slate-400 mt-2 text-sm">Welcome back. Let's get you on the turf.</p>

                    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Email</label>
                            <input type="email" required value={email}
                                onChange={(e) => { setEmail(e.target.value); validateEmail(e.target.value); }}
                                onBlur={(e) => validateEmail(e.target.value)}
                                placeholder="you@email.com" data-testid="login-email-input"
                                className={`w-full mt-2 h-11 px-4 bg-slate-900 border rounded text-white focus:outline-none focus:ring-2 ${
                                    emailErr ? "border-red-700 focus:ring-red-500" : "border-slate-700 focus:ring-[#00FF66]"
                                }`} />
                            {emailErr && <p className="mt-1 text-xs text-red-400" data-testid="login-email-error">{emailErr}</p>}
                            {emailSuggest && (
                                <button type="button" onClick={() => { setEmail(emailSuggest); setEmailSuggest(""); validateEmail(emailSuggest); }}
                                    className="mt-1 text-xs text-[#00FF66] hover:underline" data-testid="email-suggestion-btn">
                                    Did you mean <b>{emailSuggest}</b>?
                                </button>
                            )}
                        </div>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Password</label>
                            <div className="mt-2">
                                <PasswordInput value={password} onChange={setPassword}
                                    testid="login-password-input" autoComplete="current-password" />
                            </div>
                            <div className="mt-2 text-right">
                                <button type="button" onClick={() => setShowForgot(true)} data-testid="forgot-password-link"
                                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-slate-400 hover:text-[#00FF66] font-semibold">
                                    <KeyRound className="w-3 h-3" /> Forgot password?
                                </button>
                            </div>
                        </div>
                        <button type="submit" disabled={loading || !!emailErr}
                            data-testid="login-submit-btn"
                            className="w-full h-11 bg-[#00FF66] text-black font-bold uppercase tracking-wide rounded hover:bg-[#00cc52] transition active:scale-95 disabled:opacity-50 glow-green">
                            {loading ? "Signing in..." : "Sign in"}
                        </button>
                    </form>

                    <div className="my-6 flex items-center gap-3">
                        <span className="flex-1 h-px bg-slate-800" />
                        <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-slate-500">OR</span>
                        <span className="flex-1 h-px bg-slate-800" />
                    </div>

                    <GoogleLoginButton />

                    <p className="mt-8 text-center text-sm text-slate-400">
                        New here?{" "}
                        <Link to="/register" data-testid="register-link" className="text-[#00FF66] font-semibold hover:underline">Create account</Link>
                    </p>

                    <div className="mt-8 pt-5 border-t border-slate-800 text-center text-[11px] text-slate-500 leading-relaxed" data-testid="login-brand-footer">
                        <div className="flex items-center justify-center gap-3 text-[11px]">
                            <Link to="/terms" data-testid="login-footer-terms" className="text-slate-400 hover:text-[#00FF66] underline-offset-2 hover:underline">Terms &amp; Conditions</Link>
                            <span className="text-slate-700">·</span>
                            <Link to="/privacy" data-testid="login-footer-privacy" className="text-slate-400 hover:text-[#00FF66] underline-offset-2 hover:underline">Privacy Policy</Link>
                        </div>
                        <div className="mt-3 text-[10px] text-slate-500 leading-relaxed">
                            Drive or Kick is a brand operated by ALL SPORTS.<br />
                            © 2026 ALL SPORTS. All Rights Reserved.
                        </div>
                    </div>
                </div>
            </div>

            {/* Forgot password modal (player flow) */}
            {showForgot && (
                <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-lg p-6 relative" data-testid="forgot-modal">
                        <button onClick={closeForgot} className="absolute top-3 right-3 text-slate-500 hover:text-white" data-testid="forgot-close-btn">
                            <X className="w-4 h-4" />
                        </button>
                        {forgotSent ? (
                            <div className="text-center" data-testid="forgot-sent">
                                <div className="w-12 h-12 mx-auto rounded-full bg-[#00FF66]/15 border border-[#00FF66]/40 flex items-center justify-center mb-3">
                                    <KeyRound className="w-5 h-5 text-[#00FF66]" />
                                </div>
                                <h3 className="font-display text-lg font-bold uppercase text-white">Check your inbox</h3>
                                <p className="text-sm text-slate-400 mt-2">
                                    If an account exists for <b className="text-white">{forgotEmail}</b>, we've sent a password reset link via Email and WhatsApp. The link expires in 30 minutes.
                                </p>
                                <button onClick={closeForgot} data-testid="forgot-done-btn"
                                    className="mt-5 w-full h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded hover:bg-[#00cc52]">
                                    Done
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submitForgot} className="space-y-4" data-testid="forgot-form">
                                <h3 className="font-display text-lg font-bold uppercase text-white">Forgot Password</h3>
                                <p className="text-xs text-slate-400">Enter your registered email. We'll send a reset link via Email + WhatsApp.</p>
                                <div>
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Email</label>
                                    <input type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                                        placeholder="you@email.com" data-testid="forgot-email-input" autoComplete="off"
                                        className="w-full mt-2 h-11 px-4 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                                </div>
                                <button type="submit" disabled={forgotBusy} data-testid="forgot-submit-btn"
                                    className="w-full h-11 bg-[#00FF66] text-black font-bold uppercase tracking-wide rounded hover:bg-[#00cc52] disabled:opacity-50">
                                    {forgotBusy ? "Sending…" : "Send Reset Link"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
